import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

// Parse a 'yyyy-MM-dd' (or ISO) string into a local Date, avoiding timezone shift.
function parseDateValue(value) {
  if (!value) return undefined
  const str = String(value).slice(0, 10)
  const d = new Date(`${str}T00:00:00`)
  return isNaN(d.getTime()) ? undefined : d
}

// Format a Date back to 'yyyy-MM-dd' using local parts (no timezone shift).
function toISODate(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

/**
 * DateInput — shadcn Calendar replacement for native <input type="date">.
 * Keeps the native input API: controlled `value` ('yyyy-MM-dd') and
 * `onChange` that fires with a synthetic `{ target: { value } }` event, so
 * existing `onChange={(e) => ...e.target.value}` handlers keep working.
 */
const DateInput = React.forwardRef(
  ({ className, value, onChange, disabled, min, max, placeholder = "Pick a date", ...props }, ref) => {
    const [open, setOpen] = React.useState(false)
    const selected = parseDateValue(value)
    const minDate = parseDateValue(min)
    const maxDate = parseDateValue(max)

    const disabledMatcher =
      minDate || maxDate
        ? (date) => (minDate && date < minDate) || (maxDate && date > maxDate)
        : undefined

    const emit = (d) => {
      onChange?.({ target: { value: d ? toISODate(d) : "" } })
      setOpen(false)
    }

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            ref={ref}
            disabled={disabled}
            className={cn(
              "flex h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
              !selected && "text-muted-foreground",
              className
            )}
            {...props}
          >
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
            <span className="truncate">
              {selected ? format(selected, "d MMM yyyy") : placeholder}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={emit}
            disabled={disabledMatcher}
            defaultMonth={selected}
            initialFocus
          />
          {selected && (
            <div className="border-t p-2">
              <button
                type="button"
                className="w-full rounded-sm py-1 text-xs text-muted-foreground hover:bg-accent"
                onClick={() => emit(undefined)}
              >
                Clear
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    )
  }
)
DateInput.displayName = "DateInput"

const Input = React.forwardRef(({ className, type, ...props }, ref) => {
  if (type === "date") {
    return <DateInput className={className} ref={ref} {...props} />
  }
  return (
    <input
      type={type}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      {...props} />
  );
})
Input.displayName = "Input"

export { Input }
